import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import type { LineUsage } from '@erp/rpc';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { customerSalesReasonLabel } from '../../forecast/details/panelFormat';
import {
  DEPENDENT_USAGE_COLUMN_ID,
  USAGE_12_COLUMN_ID,
} from '../utils/buyerBoardColumns';
import { usageQuantity, type UsagePart } from '../utils/lineUsage';

type Translate = ReturnType<typeof useGT>;

// The own figure is the board's one default number (GEN-3165), so its hover
// carries what rolls up from the locations this one restocks.
function dependentSummary(
  dependent: { quantity: number; locationCount: number } | null,
  gt: Translate,
): string {
  if (!dependent)
    return gt('No locations restock from this one', {
      context:
        'buyer board 12-month usage hover, the location has no dependents',
    });
  return gt(
    'Dependent usage {quantity} from {count, plural, one {# location} other {# locations}}',
    {
      context:
        'buyer board 12-month usage hover, customer sales of the locations that restock from this one',
      quantity: dependent.quantity.toLocaleString(),
      count: dependent.locationCount,
    },
  );
}

function usageTitle(
  usage: LineUsage | null,
  part: UsagePart,
  gt: Translate,
): string | undefined {
  if (usage?.availability === 'unavailable')
    return customerSalesReasonLabel(usage.reason, gt);
  if (usage?.availability !== 'available') return undefined;
  if (part === 'own') return dependentSummary(usage.dependent, gt);
  if (usage.dependent)
    return gt('{count, plural, one {# location} other {# locations}}', {
      context:
        'buyer board dependent usage hover, how many locations restock from this one',
      count: usage.dependent.locationCount,
    });
  return undefined;
}

/**
 * The hint opens on hover and on keyboard focus, so the cell takes a tab stop;
 * screen readers get the same text, since they do not reach the tooltip.
 */
function UsageHint({ hint, children }: { hint: string; children: ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className="rounded outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
          >
            {children}
            <span className="sr-only">{hint}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function LineUsageCell({
  usage,
  part = 'own',
}: {
  usage: LineUsage | null;
  part?: UsagePart;
}) {
  const gt = useGT();
  const quantity = usageQuantity(usage, part);
  const hint = usageTitle(usage, part, gt);
  const value =
    quantity === null ? (
      <span className="text-xs text-gray-300">—</span>
    ) : (
      <span className="text-xs tabular-nums">{quantity.toLocaleString()}</span>
    );
  return hint ? <UsageHint hint={hint}>{value}</UsageHint> : value;
}

function usageHeaderHint(id: string, gt: Translate): string | undefined {
  if (id === USAGE_12_COLUMN_ID)
    return gt(
      'Customer sales at this location over the last 12 complete months, net of returns; transfers excluded. Hover a value for dependent usage.',
      { context: 'buyer board 12-month usage column header hint' },
    );
  if (id === DEPENDENT_USAGE_COLUMN_ID)
    return gt(
      'Customer sales at every location that restocks from this one, directly or through others, over the same months.',
      { context: 'buyer board dependent usage column header hint' },
    );
  return undefined;
}

/** What a usage column counts, on its header's hover; other headers as they are. */
export function UsageHeaderHint({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  const gt = useGT();
  const hint = usageHeaderHint(id, gt);
  return hint ? <div title={hint}>{children}</div> : children;
}
